<template>
  <CustomModal v-if="open" @close="$emit('close')" class="invest-modal">
    <template v-slot:body>
      <LeftGradientBlockLayout>
        <LeftGradientBlockLayoutHead>
          <template slot="title">
            <span class="whitespace-no-wrap">{{ $t('modalTitle') }}</span>
          </template>
        </LeftGradientBlockLayoutHead>
        <LeftGradientBlockLayoutBody>
          <template slot="left">
            <ValueInput v-model.trim="$v.price.$model"
                        label="Цена"
                        :currency_id="instrumentCurrencyId"
                        placeholder="0.00"
                        :class="{'--error':$v.price.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.price.required">Поле обязательно</div>
              </template>
            </ValueInput>
            <NumberInput v-model.trim="$v.amount.$model"
                         label="Количество"
                         :decimals="0"
                         :class="{'--error':$v.amount.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.amount.required">Поле обязательно</div>
              </template>
            </NumberInput>
            <ValueInput v-model.trim="commission_broker"
                        :currency_id="instrumentCurrencyId"
                        label="Комиссия"
                        :class="{'--error':$v.commission_broker.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.commission_broker.required">{{ $t('errors.required') }}</div>
              </template>
            </ValueInput>
            <ValueInput v-model="dealSum"
                        label="Сумма сделки"
                        :read-only-style="true"
                        :currency_id="instrumentCurrencyId"/>
          </template>
          <template slot="right">
            <BaseSelect v-model="instrument_type"
                        :options="instrument_type_list"
                        label="title"
                        select-label="Тип инструмента"
                        placeholder="Выберите..."
                        :internal-search="false"
                        :allow-empty="false"
                        :close-on-select="true"
                        :show-labels="false"
                        :show-no-results="false"
                        @input="selectInstrumentType($event)"/>
            <BaseSelect v-model="selected_instrument"
                        :options="instruments_list"
                        :loading="isLoading"
                        class="col-span-2"
                        :class="{'--error':$v.selected_instrument.$error}"
                        label="name"
                        select-label="Код или название бумаги(компании)"
                        track-by="id"
                        placeholder="Введите название"
                        :internal-search="false"
                        :allow-empty="false"
                        :close-on-select="true"
                        :show-labels="false"
                        :show-no-results="true"
                        :formatted-option="instrumentFormattedOption"
                        @input="selectSymbol"
                        @search-change="asyncFindInstrument">
              <template v-slot:errors>
                <div class="error" v-if="$v.selected_instrument.$invalid">{{ $t('errors.required') }}</div>
              </template>
              <template v-slot:noResult="">
                Ничего не удалось найти
              </template>
              <template v-slot:noOptions="">
                Начните поиск...
              </template>
              <template v-slot:option="props">
                {{ instrumentFormattedOption(props) }}
              </template>
              <template v-slot:singleLabel="props">
                {{ instrumentFormattedOption(props) }}
              </template>
            </BaseSelect>
            <BaseTextarea class="col-span-2"
                          v-model="note"
                          label="Заметка"
                          placeholder=""/>
            <BaseButton theme="red"
                        @click="addDeal"
                        :loading="submitted">
              {{ $t('btnLabel') }}
            </BaseButton>
            <div class="flex items-center">
              <VueCtkDateTimePicker id="transaction-date"
                                    class="inline"
                                    v-model="date"
                                    format="YYYY-MM-DD"
                                    :formatted="dateFormatted"
                                    :locale="$i18n.locale"
                                    :auto-close="true"
                                    :only-date="true"
                                    :no-label="true"
                                    :no-clear-button="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
              <VueCtkDateTimePicker id="transaction-time"
                                    class="inline"
                                    v-model="time"
                                    format="HH:mm"
                                    formatted="HH:mm"
                                    :locale="$i18n.locale"
                                    :only-time="true"
                                    :no-clear-button="true"
                                    :no-label="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
            </div>
          </template>
        </LeftGradientBlockLayoutBody>
      </LeftGradientBlockLayout>
    </template>
  </CustomModal>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {required} from 'vuelidate/lib/validators'
import CustomModal from "@/components/UI/modals/CustomModal";
import LeftGradientBlockLayout from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayout";
import LeftGradientBlockLayoutHead
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutHead";
import LeftGradientBlockLayoutBody
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutBody";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseTextarea from "@/components/UI/BaseTextarea";
import NumberInput from "@/components/UI/BaseInput/NumberInput";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import {dealTypes} from "@/utilities/invest_deal_types";

export default {
  name: "AssetSellModal",
  components: {
    ValueInput,
    LeftGradientBlockLayoutBody,
    LeftGradientBlockLayoutHead,
    LeftGradientBlockLayout,
    NumberInput,
    BaseTextarea,
    BaseButton,
    CustomModal,
    BaseSelect,
  },
  props: {
    open: {
      required: true
    },
  },
  data() {
    return {
      selected_instrument: null,
      instruments_list: [],
      isLoading: false,

      instrument_type: {val: 1, title: 'Акция'},
      instrument_type_list: [
        {val: 1, title: 'Акция'},
        {val: 2, title: 'Облигация'},
        {val: 3, title: 'Фонд'},
        {val: 4, title: 'Валюта'}
      ],

      date: this.$moment().format('YYYY-MM-DD'),
      time: this.$moment().format('HH:mm'),

      price: null,
      amount: null,
      commission_broker: null,
      note: '',

      submitted: false,
    }
  },
  computed: {
    ...mapState('investments/portfolios', ['current_portfolio']),
    ...mapState('investments/assets', ['assets_list']),
    ...mapGetters('cabinet/currencies', ['getCurrencyByCode']),
    dateFormatted() {
      const isToday = this.$moment(this.date, 'YYYY-MM-DD').startOf('day').unix() === this.$moment().startOf('day').unix()

      return isToday ? this.$t('today') : 'DD.MM.YYYY'
    },
    instrumentCurrencyId() {
      if (!!this.selected_instrument) {
        return this.getCurrencyByCode(this.selected_instrument.currency).id
      } else {
        return '';
      }
    },
    dealSum() {
      if (this.instrument_type.val === 2) {
        return (+this.price * this.amount) + +this.commission_broker + +this.nkd;
      } else {
        return (+this.price * this.amount) + +this.commission_broker;
      }
    }
  },
  validations: {
    selected_instrument:{
      required
    },
    price: {
      required
    },
    amount: {
      required
    },
    commission_broker: {
      required
    }
  },
  methods: {
    ...mapActions('investments/instruments', ['searchInstrument', 'addSymbol']),
    ...mapActions('investments/deals', ['createDeal']),
    selectInstrumentType(new_instrument_type) {
      if (!!this.selected_instrument && new_instrument_type.val !== this.selected_instrument.type) {
        this.selected_instrument = null;
      }
      this.instrument_type = new_instrument_type
    },
    asyncFindInstrument(query) {
      this.isLoading = true
      this.searchInstrument({type: this.instrument_type.val, query}).then(res => {
        this.instruments_list = res.body.data.rows;
        this.isLoading = false;
      })
    },
    instrumentFormattedOption(val) {
      return `${val.option.name} (${val.option.ticker}:${val.option.exchange})`
    },
    selectSymbol(val) {
      if (val.type !== 2) {
        this.value = null;
        this.nkd = null;
      }
    },
    addDeal() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          portfolio_id: this.current_portfolio.data.id,
          instrument_id: this.selected_instrument.id,
          type: dealTypes.TYPE_SELL,

          price: this.price,
          amount: this.amount,
          commission_broker: +this.commission_broker,

          note: this.note,
          date_time: `${this.date} ${this.time}:00`,
          timezone: this.$moment.tz.guess(),
        }
        this.createDeal(data).then(res => {
          if (res.body.success) {
            this.$store.dispatch('investments/assets/getAssets', this.current_portfolio.data.id)
            this.$store.dispatch('investments/assets/getGroupedAssets', this.current_portfolio.data.id)
            this.$emit('close')
            this.resetFields();
            this.$v.$reset();
          }
        })
      }
    },
    resetFields() {
      this.selected_instrument = null
      this.instruments_list = []
      this.isLoading = false
      this.instrument_type = {val: 1, title: 'Акция'}
      this.date = this.$moment().format('YYYY-MM-DD')
      this.time = this.$moment().format('HH:mm')
      this.asset = null
      this.price = null
      this.amount = null
      this.commission_broker = null
      this.note = ''
      this.value = null
      this.nkd = null
      this.submitted = false
    }
  },
  watch: {
    open(val) {
      if (!val) {
        this.resetFields();
        this.$v.$reset();
      }
    }
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Sell asset',
        btnLabel: 'Sell',
        today: '[Today]'
      },
      ru: {
        modalTitle: 'Продать актив',
        btnLabel: 'Продать',
        today: 'Сегодня'
      }
    }
  }
}
</script>